// Ajustar uma imagem antes de usar como ícone de página ou foto de perfil:
// arrastar para enquadrar, zoom e prévia de como fica em cada lugar do app.
// Devolve {blob, name} (PNG quadrado) ou null se a pessoa cancelar.

import { el, dialog, toast } from "./dom.js";
import { ui } from "./icons.js";
import { uploadFile } from "./api.js";

const VIEW = 300; // área de edição (px)
const CROP = 232; // recorte dentro dela
const M = (VIEW - CROP) / 2;

export function loadImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("não deu para abrir esta imagem")); };
    img.src = url;
  });
}

// shape: "square" (ícone) | "circle" (foto de perfil)
export async function adjustImage(file, { shape = "square", size = 256, title } = {}) {
  let loaded;
  try {
    loaded = await loadImage(file);
  } catch (err) {
    toast(err.message);
    return null;
  }
  const { img, url } = loaded;
  const iw = img.naturalWidth || 256;
  const ih = img.naturalHeight || 256;
  const cover = CROP / Math.min(iw, ih);
  const contain = CROP / Math.max(iw, ih);
  // Ícone pode diminuir até a imagem inteira caber (sobra transparente);
  // a foto de perfil sempre preenche o círculo.
  const min = shape === "circle" ? cover : Math.min(contain, cover);
  const max = cover * 6;
  let scale = cover;
  let x = (VIEW - iw * scale) / 2;
  let y = (VIEW - ih * scale) / 2;

  const dpr = window.devicePixelRatio || 1;
  const stage = el("canvas", { class: "adjust-stage", width: VIEW * dpr, height: VIEW * dpr, style: { width: VIEW + "px", height: VIEW + "px" } });
  const zoom = el("input", { type: "range", min: 0, max: 1000, value: toSlider(scale), class: "adjust-zoom", "aria-label": "Zoom" });
  const previews = shape === "circle" ? avatarPreviews() : iconPreviews();

  function toSlider(s) { return Math.round((Math.log(s / min) / Math.log(max / min || 1.0001)) * 1000) || 0; }
  function fromSlider(v) { return min * Math.pow(max / min, v / 1000); }

  const clampAxis = (pos, len) => (len >= CROP ? Math.min(M, Math.max(M + CROP - len, pos)) : Math.min(M + CROP - len, Math.max(M, pos)));
  const clamp = () => { x = clampAxis(x, iw * scale); y = clampAxis(y, ih * scale); };

  // Desenha o recorte num canvas de lado `out` (prévias e resultado final).
  const paint = (ctx, out) => {
    const f = out / CROP;
    ctx.clearRect(0, 0, out, out);
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, (x - M) * f, (y - M) * f, iw * scale * f, ih * scale * f);
  };

  const draw = () => {
    clamp();
    const ctx = stage.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, VIEW, VIEW);
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, x, y, iw * scale, ih * scale);
    // Escurece o que fica fora do recorte.
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, VIEW, VIEW);
    if (shape === "circle") ctx.arc(VIEW / 2, VIEW / 2, CROP / 2, 0, Math.PI * 2, true);
    else roundRect(ctx, M, M, CROP, CROP, 14, true);
    ctx.fillStyle = "rgba(15, 15, 15, 0.55)";
    ctx.fill("evenodd");
    ctx.restore();
    ctx.strokeStyle = "rgba(255,255,255,0.9)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    if (shape === "circle") ctx.arc(VIEW / 2, VIEW / 2, CROP / 2, 0, Math.PI * 2);
    else roundRect(ctx, M, M, CROP, CROP, 14);
    ctx.stroke();
    for (const c of previews.canvases) {
      const px = Number(c.dataset.size) * dpr;
      if (c.width !== px) { c.width = px; c.height = px; }
      paint(c.getContext("2d"), px);
    }
  };

  const zoomAt = (next, cx = VIEW / 2, cy = VIEW / 2) => {
    next = Math.min(max, Math.max(min, next));
    x = cx - ((cx - x) * next) / scale;
    y = cy - ((cy - y) * next) / scale;
    scale = next;
    zoom.value = toSlider(scale);
    draw();
  };

  let dragFrom = null;
  stage.addEventListener("pointerdown", (e) => { dragFrom = { px: e.clientX, py: e.clientY, x, y }; stage.setPointerCapture(e.pointerId); stage.classList.add("dragging"); });
  stage.addEventListener("pointermove", (e) => {
    if (!dragFrom) return;
    x = dragFrom.x + e.clientX - dragFrom.px;
    y = dragFrom.y + e.clientY - dragFrom.py;
    draw();
  });
  const endDrag = () => { dragFrom = null; stage.classList.remove("dragging"); };
  stage.addEventListener("pointerup", endDrag);
  stage.addEventListener("pointercancel", endDrag);
  stage.addEventListener("wheel", (e) => {
    e.preventDefault();
    const r = stage.getBoundingClientRect();
    zoomAt(scale * Math.exp(-e.deltaY * 0.0015), e.clientX - r.left, e.clientY - r.top);
  }, { passive: false });
  zoom.addEventListener("input", () => zoomAt(fromSlider(Number(zoom.value))));

  const keys = (e) => {
    const step = e.shiftKey ? 10 : 2;
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    if (moves[e.key]) { e.preventDefault(); x += moves[e.key][0]; y += moves[e.key][1]; draw(); }
    if (e.key === "+" || e.key === "=") zoomAt(scale * 1.1);
    if (e.key === "-") zoomAt(scale / 1.1);
  };

  return new Promise((resolve) => {
    let result = null;
    const finish = async (original) => {
      if (original) {
        result = { blob: file, name: file.name || "imagem" };
      } else {
        const out = el("canvas", { width: size, height: size });
        paint(out.getContext("2d"), size);
        const blob = await new Promise((r) => out.toBlob(r, "image/png"));
        result = blob ? { blob, name: (file.name || "imagem").replace(/\.[^.]+$/, "") + ".png" } : null;
      }
      dlg.close();
    };
    const dlg = dialog((close) => [
      el("h3", {}, title || (shape === "circle" ? "Ajustar foto" : "Ajustar ícone")),
      el("p", { class: "muted small" }, "Arraste a imagem para enquadrar e use o controle (ou a rodinha do mouse) para aproximar."),
      el("div", { class: "adjust" },
        el("div", { class: "adjust-left" },
          stage,
          el("div", { class: "adjust-zoom-row" },
            el("button", { class: "icon-btn", type: "button", title: "Afastar", onclick: () => zoomAt(scale / 1.15) }, ui("zoomOut", 16)),
            zoom,
            el("button", { class: "icon-btn", type: "button", title: "Aproximar", onclick: () => zoomAt(scale * 1.15) }, ui("zoomIn", 16)))),
        el("div", { class: "adjust-right" }, el("div", { class: "picker-label" }, "Como vai aparecer"), previews.node)),
      el("div", { class: "dlg-actions" },
        /gif$/i.test(file.type || "") ? el("span", { class: "muted small grow" }, "GIF animado: use \"Usar sem ajustar\" para manter a animação.") : el("span", { class: "grow" }),
        el("button", { type: "button", onclick: () => finish(true) }, "Usar sem ajustar"),
        el("button", { type: "button", onclick: close }, "Cancelar"),
        el("button", { type: "button", class: "primary", onclick: () => finish(false) }, "Salvar")),
    ], { className: "adjust-dialog" });
    dlg.addEventListener("keydown", keys);
    dlg.addEventListener("close", () => { URL.revokeObjectURL(url); resolve(result); });
    draw();
    requestAnimationFrame(() => stage.focus?.());
  });
}

// Ajusta e envia: devolve a URL da imagem salva, ou null.
export async function adjustAndUpload(file, opts) {
  const out = await adjustImage(file, opts);
  if (!out) return null;
  toast("Enviando a imagem…", 60000);
  try {
    const { url } = await uploadFile(out.blob, out.name);
    toast("Imagem salva.", 1500);
    return url;
  } catch (err) {
    toast("Não foi possível enviar: " + err.message);
    return null;
  }
}

// Imagem colada (Ctrl+V) ou arrastada: o primeiro arquivo de imagem.
export function imageFromTransfer(dt) {
  if (!dt) return null;
  for (const f of dt.files || []) if (/^image\//.test(f.type)) return f;
  for (const it of dt.items || []) {
    if (it.kind === "file" && /^image\//.test(it.type)) {
      const f = it.getAsFile();
      if (f) return f.name ? f : new File([f], `imagem-colada.${(it.type.split("/")[1] || "png").replace("svg+xml", "svg")}`, { type: it.type });
    }
  }
  return null;
}

// Link de imagem colado (texto ou <img src> de uma página copiada).
export function imageUrlFromTransfer(dt) {
  const text = dt?.getData("text/plain")?.trim() || "";
  if (/^https:\/\/\S+$/i.test(text)) return text;
  const html = dt?.getData("text/html") || "";
  const m = /<img[^>]+src="(https:\/\/[^"]+)"/i.exec(html);
  return m ? m[1].replace(/&amp;/g, "&") : null;
}

function roundRect(ctx, x, y, w, h, r, reverse = false) {
  if (reverse) {
    // Sentido anti-horário, para o recorte "furar" o retângulo de fora.
    ctx.moveTo(x + r, y);
    ctx.arcTo(x, y, x, y + r, r);
    ctx.lineTo(x, y + h - r);
    ctx.arcTo(x, y + h, x + r, y + h, r);
    ctx.lineTo(x + w - r, y + h);
    ctx.arcTo(x + w, y + h, x + w, y + h - r, r);
    ctx.lineTo(x + w, y + r);
    ctx.arcTo(x + w, y, x + w - r, y, r);
    ctx.closePath();
    return;
  }
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

const pv = (size, cls = "") => el("canvas", { class: "pv " + cls, "data-size": size, style: { width: size + "px", height: size + "px" } });

function iconPreviews() {
  const big = pv(78, "pv-big");
  const side = pv(18);
  const link = pv(20);
  const dark = pv(18);
  const node = el("div", { class: "adjust-previews" },
    el("div", { class: "pv-card" }, el("div", { class: "pv-caption" }, "No topo da página"), el("div", { class: "pv-title" }, big, el("span", {}, "Título da página"))),
    el("div", { class: "pv-card" }, el("div", { class: "pv-caption" }, "Na barra lateral"), el("div", { class: "pv-row" }, side, el("span", {}, "Título da página"))),
    el("div", { class: "pv-card dark" }, el("div", { class: "pv-caption" }, "No tema escuro"), el("div", { class: "pv-row" }, dark, el("span", {}, "Título da página"))),
    el("div", { class: "pv-card" }, el("div", { class: "pv-caption" }, "Em links e menções"), el("div", { class: "pv-row pv-link" }, link, el("span", {}, "Título da página"))));
  return { node, canvases: [big, side, link, dark] };
}

function avatarPreviews() {
  const big = pv(88, "round");
  const side = pv(22, "round");
  const small = pv(18, "round");
  const node = el("div", { class: "adjust-previews" },
    el("div", { class: "pv-card" }, el("div", { class: "pv-caption" }, "No seu perfil"), big),
    el("div", { class: "pv-card" }, el("div", { class: "pv-caption" }, "Na barra lateral"), el("div", { class: "pv-row" }, side, el("span", {}, "Seu nome"))),
    el("div", { class: "pv-card" }, el("div", { class: "pv-caption" }, "Em comentários e tarefas"), el("div", { class: "pv-row" }, small, el("span", { class: "muted small" }, "comentou agora"))));
  return { node, canvases: [big, side, small] };
}
